// Composant de player vidéo HLS
import React from 'react';
import { useHLSPlayer, type UseHLSPlayerOptions } from '../Hooks/useHLSPlayer';

export interface HLSVideoPlayerProps extends UseHLSPlayerOptions {
    fileId?: string;
    src?: string;
    className?: string;
    style?: React.CSSProperties;
    controls?: boolean;
    poster?: string;
    onReady?: () => void;
    preferredQuality?: 'low' | 'medium' | 'high' | 'auto';  // Qualité préférée au démarrage
    instanceId?: string; // ID de l'instance pour le cache
    componentId?: string; // ID du composant pour le cache
}

/**
 * Composant de player vidéo avec support HLS
 * - Détection automatique API vs Local
 * - Support HLS avec qualités adaptatives
 * - Fallback automatique si erreur
 */
export function HLSVideoPlayer({
    fileId,
    src,
    className = '',
    style = {},
    controls = true,
    poster,
    autoPlay = false,
    fallbackSrc,
    onReady,
    instanceId,
    componentId
}: HLSVideoPlayerProps) {
    // Utiliser fileId ou src
    const source = fileId || src;

    const { videoRef, isReady, error, qualities, currentQuality } = useHLSPlayer(
        source,
        {
            autoPlay,
            fallbackSrc,
            instanceId,
            componentId
        }
    );

    // Effet onReady
    React.useEffect(() => {
        if (isReady && onReady) {
            onReady();
        }
    }, [isReady, onReady]);

    return (
        <div
            className={`hls-video-player-container ${className}`}
            style={{ position: 'relative', ...style }}
        >
            <video
                ref={videoRef}
                className="hls-video-player"
                controls={controls}
                poster={poster}
                style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#000'
                }}
            />

            {!isReady && !error && (
                <div
                    className="hls-video-loader"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        color: 'white'
                    }}
                >
                    <div className="spinner"></div>
                    <p style={{ marginTop: '16px' }}>Chargement de la vidéo...</p>
                </div>
            )}

            {error && (
                <div
                    className="hls-video-error"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0,0,0,0.9)',
                        color: 'white',
                        padding: '20px',
                        textAlign: 'center'
                    }}
                >
                    <span style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</span>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
                        Erreur de chargement
                    </p>
                    <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>
                        {error.message}
                    </p>
                    {fallbackSrc && (
                        <p style={{ margin: '16px 0 0 0', fontSize: '12px' }}>
                            Lecture en mode standard...
                        </p>
                    )}
                </div>
            )}

            {isReady && qualities.length > 0 && (
                <div
                    className="hls-video-quality-badge"
                    style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        pointerEvents: 'none'
                    }}
                >
                    {currentQuality >= 0 ? qualities[currentQuality] : 'Auto'}
                    {qualities.length > 1 && ` (${qualities.length} qualités)`}
                </div>
            )}
        </div>
    );
}

